import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Send, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { mockData } from '../mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [organization, setOrganization] = useState(mockData.organization);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    fetchOrganization();
  }, []);

  const fetchOrganization = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/organization`);
      setOrganization(response.data);
    } catch (error) {
      // Organization fetch failed - non-critical, continue with defaults
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/submissions/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully! We'll get back to you within 24 hours.");
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generate Google Maps URL from address
  const getGoogleMapsUrl = (address) => {
    const encodedAddress = encodeURIComponent(address);
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
    return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodedAddress}&zoom=15`;
  };

  // Generate Google Maps link for directions
  const getDirectionsUrl = (address) => {
    const encodedAddress = encodeURIComponent(address);
    return `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
  };

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img 
            src="/images/contact-image.png"
            alt="Contact Us - Phone, Email, Mail"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A345E]/70 to-[#007A7A]/60"></div>
        </div>
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Let's Talk</h1>
          <p className="text-xl text-white/95 leading-relaxed">
            Have questions or want to learn more? We'd like to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-0 shadow-lg bg-[#F5F2E8]">
                <CardHeader>
                  <CardTitle className="text-2xl text-[#0A345E]">Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#0A345E] to-[#007A7A] flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0A345E] mb-1">Address</h3>
                      <p className="text-gray-600 text-sm">{organization.address}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#0A345E] to-[#007A7A] flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0A345E] mb-1">Phone</h3>
                      <p className="text-gray-600 text-sm">{organization.phone}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#0A345E] to-[#007A7A] flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0A345E] mb-1">Email</h3>
                      <p className="text-gray-600 text-sm">{organization.email}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#0A345E] to-[#007A7A] flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0A345E] mb-1">Hours</h3>
                      <p className="text-gray-600 text-sm">
                        {organization.office_hours || 'Monday – Friday, 9am – 5pm CT'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Interactive Map */}
              <Card className="border-0 shadow-lg overflow-hidden">
                <div className="h-64 relative">
                  <iframe
                    title="Gateway Inc. Location"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3000!2d-74.006!3d40.7128!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s${encodeURIComponent(organization.address || '123 Hope Street, Community City')}!5e0!3m2!1sen!2sus!4v1`}
                    data-testid="google-map-iframe"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-4">
                    <a
                      href={getDirectionsUrl(organization.address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-white text-sm font-medium hover:underline"
                      data-testid="get-directions-link"
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      Get Directions
                    </a>
                  </div>
                </div>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-2xl">
                <CardHeader className="bg-gradient-to-r from-[#0A345E] to-[#007A7A] text-white rounded-t-lg">
                  <CardTitle className="text-3xl">Get In Touch</CardTitle>
                </CardHeader>
                <CardContent className="p-8">
                  {submitted ? (
                    <div className="text-center py-12" data-testid="contact-success-message">
                      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                        <CheckCircle className="w-10 h-10 text-green-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-[#0A345E] mb-3">Message Sent!</h3>
                      <p className="text-gray-600 mb-6">Thank you for contacting us. We'll get back to you within 24 hours.</p>
                      <Button
                        onClick={() => setSubmitted(false)}
                        variant="outline"
                        className="border-[#007A7A] text-[#007A7A]"
                      >
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007A7A] focus:border-transparent transition-all duration-200"
                        placeholder="John Doe"
                        data-testid="contact-name-input"
                      />
                    </div>

                    {/* Email & Phone */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007A7A] focus:border-transparent transition-all duration-200"
                          placeholder="john@example.com"
                          data-testid="contact-email-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007A7A] focus:border-transparent transition-all duration-200"
                          placeholder="(555) 123-4567"
                          data-testid="contact-phone-input"
                        />
                      </div>
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                      <select
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007A7A] focus:border-transparent transition-all duration-200"
                        data-testid="contact-subject-select"
                      >
                        <option value="">Select an option</option>
                        <option value="General Inquiry">General Inquiry</option>
                        <option value="Volunteer Opportunities">Volunteer Opportunities</option>
                        <option value="Donation Questions">Donation Questions</option>
                        <option value="Partnership Inquiry">Partnership Inquiry</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                      <textarea
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleInputChange}
                        rows="6"
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007A7A] focus:border-transparent transition-all duration-200"
                        placeholder="Tell us how we can help you..."
                        data-testid="contact-message-input"
                      />
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-[#0A345E] to-[#007A7A] hover:from-[#0A345E]/90 hover:to-[#007A7A]/90 text-white py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300"
                      data-testid="contact-submit-btn"
                    >
                      <Send className="w-5 h-5 mr-2" />
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
